import {
  CardBody,
  CardHeader,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  Stack,
  Switch,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { DraftShareholder, IdentityType, RepresentativeRole } from '@blockpulse3/graphql/hooks';
import { SwitchIdentityType } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { useSPVForm } from '../../provider';
import { draftShareholderDefaultValues } from '../../provider/schema';
import { LegalShareholderInformationsForm } from './LegalShareholderInformationsForm';
import { NaturalShareholderInformationsForm } from './NaturalShareholderInformationsForm';

type Props = {
  /* ** Are fields disabled ** */
  isDisabled?: boolean;
  /* ** Are switches disabled ** */
  shouldDisabledSwitches?: boolean;
  /* ** Default form values for the Legal and Natural form version ** */
  defaultValues?: DraftShareholder;
  /* ** Callback on natural switch toggle ** */
  onToggleNaturalSwitch?: (isNatural: boolean) => void;
  /* ** Callback on legal switch toggle ** */
  onToggleLegalSwitch?: (isLegal: boolean) => void;
  /* ** Callback of the Cancel button ** */
  onCancel?: () => void;
  /* ** Callback of the Submit button ** */
  onSubmit?: (data: DraftShareholder) => void;
};

export function ShareholderForm({
  isDisabled = false,
  shouldDisabledSwitches = false,
  defaultValues = draftShareholderDefaultValues,
  onToggleNaturalSwitch = noop,
  onToggleLegalSwitch = noop,
  onCancel = noop,
  onSubmit = noop,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nShareholderForm = useTranslations('ShareholderForm');

  const { companyDraftContent } = useSPVForm();

  /* ** Look for representative with PRESIDENT role ** */
  const president = companyDraftContent.representatives.find(
    (rep) => rep.role === RepresentativeRole.PRESIDENT,
  );

  const isPresidentLegal = president?.type === IdentityType.COMPANY || false;

  const { user } = useUser();

  /* ** Disclosure to control the legal switch ** */
  const { isOpen: isLegal, onToggle: toggleLegalSwitch } = useDisclosure({
    defaultIsOpen: isPresidentLegal,
  });
  /* ** Disclosure to control the natural switch ** */
  const { isOpen: isNatural, onToggle: toggleNaturalSwitch } = useDisclosure({
    defaultIsOpen: false,
  });

  /* ** Natural switch callback ** */
  const handleToggleNaturalSwitch = (): void => {
    if (isLegal) {
      toggleLegalSwitch();
    }
    toggleNaturalSwitch();
    /* ** Step switch specific callback ** */
    onToggleNaturalSwitch(!isNatural);
  };

  /* ** Submit whatever representant form is being rendered ** */
  const handleFormSubmit = (data: DraftShareholder): void => {
    onSubmit(data);
  };

  /* ** Cancel whatever representant form is being rendered ** */
  const handleFormCancel = (): void => {
    onCancel();
  };

  return (
    <>
      <CardHeader as={Stack} spacing="2">
        <Heading size="lg">{i18nShareholderForm('Title')}</Heading>
        <Text color="gray.600" fontSize="lg">
          {i18nShareholderForm('SubTitle')}
        </Text>
      </CardHeader>
      <Divider />
      <CardBody as={Stack} spacing="8">
        <Stack spacing="4">
          <SwitchIdentityType
            disabled
            direction={{ base: 'column', md: 'row' }}
            value={president?.type as IdentityType}
          />
          <FormControl alignItems="center" display="flex">
            <Switch
              id="isUser"
              isChecked={isNatural}
              isDisabled={shouldDisabledSwitches}
              onChange={handleToggleNaturalSwitch}
            />
            <FormLabel htmlFor="isUser" mb="0" ml="2">
              <Text>
                {i18nGeneral('ItsMe')},
                <Text as="span" fontWeight="bold">
                  {` ${user?.firstName} ${user?.lastName}`}
                </Text>
              </Text>
            </FormLabel>
          </FormControl>
        </Stack>
        <Stack>
          {isLegal ? (
            <LegalShareholderInformationsForm
              defaultValues={defaultValues}
              isDisabled={isDisabled}
              onCancel={handleFormCancel}
              onSubmit={handleFormSubmit}
            />
          ) : (
            <NaturalShareholderInformationsForm
              defaultValues={defaultValues}
              isDisabled={isDisabled}
              onCancel={handleFormCancel}
              onSubmit={handleFormSubmit}
            />
          )}
        </Stack>
      </CardBody>
    </>
  );
}

export type ShareholderFormProps = Props;
